
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Bike } from '@/services/types';
import { DataTable } from '@/components/DataTable';
import { Loader2 } from 'lucide-react';
import { formatDate } from '@/utils/dateUtils';
import { RentalStatusBadge } from '../../rentals/components/RentalStatusBadge';
import { supabase } from '@/integrations/supabase/client';

// Интерфейс для аренд в формате совместимом с DataTable
interface RentalData {
  id: string;
  startDate: string;
  endDate: string | null;
  status: 'active' | 'completed' | 'cancelled' | 'pending';
  courierName: string;
  courierId: string;
}

export const BikeRentalsDialog: React.FC<{
  bike: Bike | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ bike, open, onOpenChange }) => {
  const [rentals, setRentals] = useState<RentalData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && bike) {
      fetchRentals();
    } else {
      // Очищаем аренды при закрытии диалога
      setRentals([]);
    }
  }, [open, bike]);

  const fetchRentals = async () => {
    if (!bike?.id) return;
    
    setLoading(true);
    
    try {
      // Получаем аренды для этого велосипеда
      const { data, error } = await supabase
        .from('rentals')
        .select(`
          *,
          couriers (
            id,
            name
          )
        `)
        .eq('bike_id', bike.id)
        .order('start_date', { ascending: false });
        
      if (error) {
        console.error('Ошибка при получении аренд:', error);
        return;
      }
      
      // Преобразуем данные для отображения
      const formattedRentals = data.map((rental: any) => ({
        id: rental.id,
        startDate: rental.start_date,
        endDate: rental.end_date,
        status: rental.status,
        courierName: rental.couriers?.name || 'Неизвестный курьер',
        courierId: rental.courier_id
      }));
      
      setRentals(formattedRentals);
    } catch (error) {
      console.error('Ошибка при получении аренд:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const columns = [
    {
      key: 'courierName',
      header: 'Курьер',
      cell: (rental: RentalData) => <span>{rental.courierName}</span>
    },
    {
      key: 'startDate',
      header: 'Дата начала',
      cell: (rental: RentalData) => <span>{formatDate(rental.startDate)}</span>
    },
    {
      key: 'endDate',
      header: 'Дата окончания',
      cell: (rental: RentalData) => <span>{rental.endDate ? formatDate(rental.endDate) : '-'}</span>
    },
    {
      key: 'status',
      header: 'Статус',
      cell: (rental: RentalData) => <RentalStatusBadge status={rental.status} />
    }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            История аренд велосипеда {bike?.model} (#{bike?.serialNumber})
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : rentals.length > 0 ? (
          <DataTable 
            data={rentals}
            columns={columns}
            searchable
            searchPlaceholder="Поиск по арендам..."
          />
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            У этого велосипеда нет истории аренд
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
